import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fishDataFromCsv as fishData } from '../data/fishDataFromCsv.js';
import EnhancedFishCard from './EnhancedFishCard.js';

const ResponsiveHome = () => {
  const [currentFishData, setCurrentFishData] = useState([]);
  
  useEffect(() => {
    // Load fish data from localStorage or fallback to default fishData
    const savedFish = localStorage.getItem('saltfishy_fish_inventory');
    if (savedFish) {
      try {
        const parsedFish = JSON.parse(savedFish);
        setCurrentFishData(parsedFish);
      } catch (error) {
        console.warn('Error loading fish data:', error);
        setCurrentFishData(fishData);
      }
    } else {
      setCurrentFishData(fishData);
    }
  }, []);

  const featuredFish = currentFishData.slice(0, 6);

  return (
    <div>
      {/* Mobile-First Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.3)), url('/images/fish-7994570_1920.jpg')`
          }}
        />

        {/* Content - Responsive Layout */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4">
          
          {/* Mobile Layout (Default) */}
          <div className="lg:hidden text-center">
            {/* Debug indicator */}
            <div className="absolute top-4 right-4 bg-red-500 text-white px-2 py-1 text-xs rounded z-50">
              MOBILE VIEW
            </div>
            {/* Mobile Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
              <span className="text-xl">🌊</span>
              <span className="text-sm font-medium text-white">Premium Marine Fish</span>
            </div>

            {/* Mobile Title */}
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">
              SaltFishy
              <span className="block text-blue-300">Marine Paradise</span>
            </h1>

            {/* Mobile Description */}
            <p className="text-lg text-white/80 mb-8 max-w-lg mx-auto leading-relaxed">
              Discover exotic marine fish from the pristine waters of the Maldives.
            </p>

            {/* Mobile CTA */}
            <div className="space-y-3">
              <Link to="/shop" className="block w-full max-w-xs mx-auto">
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-colors shadow-lg">
                  Explore Collection
                </button>
              </Link>
              <Link to="/about" className="block w-full max-w-xs mx-auto">
                <button className="w-full bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-colors border border-white/30">
                  Learn More
                </button>
              </Link>
            </div>
          </div>

          {/* Desktop Layout (lg and up) */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-12 items-center">
            {/* Debug indicator */}
            <div className="absolute top-4 right-4 bg-green-500 text-white px-2 py-1 text-xs rounded z-50">
              DESKTOP VIEW
            </div>
            {/* Desktop Left Content */}
            <div className="space-y-8">
              {/* Desktop Badge */}
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
                <span className="text-2xl">🌊</span>
                <span className="text-base font-medium text-white">Premium Marine Fish from Maldives</span>
              </div>

              {/* Desktop Title */}
              <h1 className="text-6xl xl:text-7xl font-bold text-white leading-none">
                SaltFishy
                <span className="block text-blue-300">Marine Paradise</span>
              </h1>

              {/* Desktop Description */}
              <p className="text-xl text-white/80 leading-relaxed max-w-2xl">
                Discover the finest collection of exotic marine fish from the pristine waters of the Maldives. 
                Each fish is carefully selected and quarantined for your peace of mind.
              </p>

              {/* Desktop CTA */}
              <div className="flex gap-4">
                <Link to="/shop">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors shadow-lg hover:shadow-xl">
                    Explore Collection
                  </button>
                </Link>
                <Link to="/about">
                  <button className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors border border-white/30">
                    Learn More
                  </button>
                </Link>
              </div>

              {/* Desktop Stats */}
              <div className="grid grid-cols-3 gap-6 pt-4">
                <div>
                  <div className="text-3xl font-bold text-white">50+</div>
                  <div className="text-sm text-white/60">Fish Species</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">98%</div>
                  <div className="text-sm text-white/60">Health Rate</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">5★</div>
                  <div className="text-sm text-white/60">Customer Rating</div>
                </div>
              </div>
            </div>

            {/* Desktop Right Image */}
            <div className="relative">
              <div className="relative w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/hero-fish.jpg"
                  alt="Beautiful Marine Fish"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "/images/fish-7994570_1920.jpg";
                  }}
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-200/30 rounded-full blur-xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-cyan-200/30 rounded-full blur-xl"></div>
            </div>
          </div>
        </div>

        {/* Shared animated elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Bubbles */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-4 h-4 rounded-full bg-white/10"
              style={{
                left: `${10 + i * 15}%`,
                bottom: `${10 + (i % 3) * 30}%`,
                animationDelay: `${i * 1.5}s`,
                animation: `float ${6 + i * 2}s infinite ease-in-out`,
              }}
            />
          ))}
        </div>
      </section>

      {/* Features Section - Responsive */}
      <section className="py-12 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-8 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose SaltFishy?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're passionate about marine life and committed to providing the highest quality fish.
            </p>
          </div>

          {/* Features Grid - Mobile: Stack, Desktop: Grid */}
          <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-3 gap-12">
            
            {/* Feature 1 */}
            <div className="text-center lg:block">
              <div className="flex items-start gap-4 lg:block lg:text-center">
                <div className="flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 bg-blue-100 rounded-full flex items-center justify-center lg:mx-auto lg:mb-6">
                  <span className="text-2xl lg:text-3xl">🐠</span>
                </div>
                <div className="flex-1 text-left lg:text-center">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2 lg:mb-4">Premium Quality</h3>
                  <p className="text-gray-600 text-sm lg:text-base">
                    Hand-selected healthy fish from trusted suppliers with thorough health checks.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="text-center lg:block">
              <div className="flex items-start gap-4 lg:block lg:text-center">
                <div className="flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 bg-green-100 rounded-full flex items-center justify-center lg:mx-auto lg:mb-6">
                  <span className="text-2xl lg:text-3xl">📚</span>
                </div>
                <div className="flex-1 text-left lg:text-center">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2 lg:mb-4">Expert Guidance</h3>
                  <p className="text-gray-600 text-sm lg:text-base">
                    Comprehensive care guides and support for thriving marine aquariums.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="text-center lg:block">
              <div className="flex items-start gap-4 lg:block lg:text-center">
                <div className="flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 bg-purple-100 rounded-full flex items-center justify-center lg:mx-auto lg:mb-6">
                  <span className="text-2xl lg:text-3xl">🚚</span>
                </div>
                <div className="flex-1 text-left lg:text-center">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2 lg:mb-4">Safe Delivery</h3>
                  <p className="text-gray-600 text-sm lg:text-base">
                    Specialized shipping ensures your fish arrive healthy and stress-free.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Fish Collection - Your Existing Logic */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 lg:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 mb-4">
              <span className="text-xl">🐠</span>
              <span className="text-sm font-medium">Our Collection</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Featured Marine Fish
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Browse our carefully curated collection of exotic marine fish.
            </p>
          </div>

          {/* Fish Grid - Responsive */}
          {featuredFish.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {featuredFish.map((fish, index) => (
                <div key={fish.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <EnhancedFishCard fish={fish} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <span className="text-6xl block mb-4">🐠</span>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading our collection...</h3>
              <p className="text-gray-600">Please wait while we load our beautiful marine fish</p>
            </div>
          )}

          {/* View All Link */}
          {featuredFish.length > 0 && (
            <div className="text-center mt-8 lg:mt-12">
              <Link to="/shop">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors shadow-lg hover:shadow-xl">
                  View All {currentFishData.length} Fish →
                </button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Add required CSS animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(10px) rotate(-1deg); }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.6s ease-in-out forwards;
          opacity: 0;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ResponsiveHome;
